import { useMemo } from "react";
import type { StreamingState } from "../streams/streamStore";
import type { Message, TraceItem, TraceToolCall, TraceTurn } from "../types";
import { parseToolOutput } from "../utils/toolOutput";

const FINAL_RESPONSE_DELIMITER = "---FINAL_RESPONSE---";

const parseToolInput = (args: string): Record<string, unknown> | undefined => {
  try {
    return JSON.parse(args) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

const isToolError = (raw: string): boolean => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed.success === false) return true;
    if (parsed.exit_code !== undefined && parsed.exit_code !== 0) return true;
  } catch {
    /* not JSON */
  }
  return false;
};

const isFailedToolResult = (
  rawContent: string | undefined,
  success: boolean | undefined,
): boolean => {
  if (success === false) return true;
  return rawContent ? isToolError(rawContent) : false;
};

/** Tool call ids of the steps a fast dataset's chat starts with: the agent's history, not shown. */
const PREFILL_ID = "toolu_build_";
const isPrefill = (msg: Message) =>
  msg.role === "tool"
    ? !!msg.tool_call_id?.startsWith(PREFILL_ID)
    : msg.role === "assistant" && msg.tool_calls.length > 0 && msg.tool_calls.every((tc) => tc.id.startsWith(PREFILL_ID));

const buildTraceTurnsFromMessages = (
  allMessages: Message[],
  runActive: boolean,
): TraceTurn[] => {
  const messages = allMessages.filter((m) => !isPrefill(m));
  const turns: TraceTurn[] = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];

    if (msg.role === "user") {
      turns.push({ id: msg.id, type: "user", content: msg.content ?? "" });
      i++;
      continue;
    }

    if (msg.role === "assistant") {
      const fullContent = msg.content ?? "";
      const delimIdx = fullContent.indexOf(FINAL_RESPONSE_DELIMITER);
      const narration =
        delimIdx >= 0
          ? fullContent.slice(0, delimIdx).trim()
          : fullContent.trim();
      const finalText =
        delimIdx >= 0
          ? fullContent.slice(delimIdx + FINAL_RESPONSE_DELIMITER.length).trim()
          : "";

      if (msg.tool_calls.length > 0) {
        const items: TraceItem[] = [];

        if (narration) {
          items.push({ type: "text", content: narration });
        }

        // Build a map of tool_call_id → tool result message.
        // Primary: consecutive tool messages immediately after the assistant msg.
        const toolResultMap = new Map<string, Message>();
        let j = i + 1;
        while (j < messages.length && messages[j].role === "tool") {
          const toolMsg = messages[j];
          if (toolMsg.tool_call_id) {
            toolResultMap.set(toolMsg.tool_call_id, toolMsg);
          }
          j++;
        }

        // Fallback: if any tool_call IDs are still unmatched, scan all messages
        const neededIds = new Set(msg.tool_calls.map((tc) => tc.id));
        for (const id of neededIds) {
          if (toolResultMap.has(id)) continue;
          const fallback = messages.find(
            (m) => m.role === "tool" && m.tool_call_id === id,
          );
          if (fallback) toolResultMap.set(id, fallback);
        }

        const turnIsComplete = j < messages.length;

        for (const tc of msg.tool_calls) {
          const resultMsg = toolResultMap.get(tc.id);
          const rawContent = resultMsg?.content ?? undefined;
          const isError = isFailedToolResult(rawContent, undefined);
          const traceCall: TraceToolCall = {
            id: tc.id,
            name: tc.function.name,
            status: isError
              ? "error"
              : resultMsg || turnIsComplete
                ? "complete"
                : runActive
                  ? "pending"
                  : "stopped",
            input: parseToolInput(tc.function.arguments),
            output: rawContent ? parseToolOutput(rawContent) : undefined,
            parentId: tc.parent_tool_call_id,
          };
          items.push({ type: "tool_call", toolCall: traceCall });
        }

        i = j;

        turns.push({
          id: msg.id,
          type: "assistant",
          items: items.length > 0 ? items : undefined,
          finalResponse: finalText || undefined,
        });
      } else {
        const items: TraceItem[] = [];
        if (narration && delimIdx >= 0) {
          items.push({ type: "text", content: narration });
        }

        turns.push({
          id: msg.id,
          type: "assistant",
          items: items.length > 0 ? items : undefined,
          finalResponse: (delimIdx >= 0 ? finalText : narration) || undefined,
        });
        i++;
      }
      continue;
    }

    // Skip standalone tool messages (they should have been consumed above)
    i++;
  }

  return turns;
};

const buildStreamingTurn = (
  state: StreamingState,
  runActive: boolean,
): TraceTurn | null => {
  // A stopped stream keeps its calls; one with no result did not finish.
  const unfinished = state.aborted || !runActive;
  if (!state.streamingMessage && state.renderChunks.length === 0) {
    if (!state.isStreaming) return null;
    return {
      id: `streaming-${state.streamId}`,
      type: "assistant",
      isStreaming: true,
      fadeHold: state.fadeHold,
    };
  }

  const items: TraceItem[] = [];

  for (const chunk of state.renderChunks) {
    if (chunk.type === "content") {
      const delimIdx = chunk.text.indexOf(FINAL_RESPONSE_DELIMITER);
      const textBeforeDelim =
        delimIdx >= 0 ? chunk.text.slice(0, delimIdx).trim() : chunk.text;
      if (textBeforeDelim) {
        items.push({ type: "text", content: textBeforeDelim });
      }
    } else if (chunk.type === "tool_calls") {
      for (const tc of chunk.calls) {
        if (!tc.id && !tc.function.name) continue;
        // While the model is still streaming the arguments, the JSON is
        // incomplete and there is nothing readable to show. Hold the row back
        // until it can render its final label, so it appears in one piece
        // instead of "Working..." → raw command → readable text.
        const input = parseToolInput(tc.function.arguments);
        if (chunk.phase === "pending" && input === undefined) continue;
        const result = state.toolResults.get(tc.id);
        const rawContent = result?.content ?? undefined;
        const hasToolError = isFailedToolResult(rawContent, result?.success);
        const traceCall: TraceToolCall = {
          id: tc.id,
          name: tc.function.name,
          status:
            hasToolError
              ? "error"
              : chunk.phase === "complete" && result
                ? "complete"
                : unfinished
                  ? "stopped"
                  : chunk.phase === "executing"
                    ? "running"
                    : "pending",
          input,
          output: rawContent
            ? parseToolOutput(rawContent)
            : undefined,
          duration: result?.duration_ms
            ? `${(result.duration_ms / 1000).toFixed(1)}s`
            : undefined,
          parentId: tc.parent_tool_call_id,
        };
        items.push({ type: "tool_call", toolCall: traceCall });
      }
    }
  }

  // Extract final response from the last content chunk if it has the delimiter
  let finalResponse: string | undefined;
  const fullContent = state.streamingMessage?.content ?? "";
  const delimIdx = fullContent.indexOf(FINAL_RESPONSE_DELIMITER);
  if (delimIdx >= 0) {
    finalResponse = fullContent
      .slice(delimIdx + FINAL_RESPONSE_DELIMITER.length)
      .trim();
  }

  return {
    id: `streaming-${state.streamId}`,
    type: "assistant",
    items: items.length > 0 ? items : undefined,
    finalResponse,
    isStreaming: state.isStreaming,
    fadeHold: state.fadeHold,
  };
};

/**
 * Move each subagent call under the Agent call that started it. The steps of a
 * subagent are saved as their own messages, so its calls can be in later turns
 * than the Agent call. A turn that held only subagent calls is dropped.
 */
export const nestSubagentCalls = (turns: TraceTurn[]): TraceTurn[] => {
  const calls = new Map<string, TraceToolCall>();
  for (const turn of turns) {
    for (const item of turn.items ?? []) {
      if (item.type === "tool_call") calls.set(item.toolCall.id, item.toolCall);
    }
  }

  let moved = false;
  const nested: TraceTurn[] = [];
  for (const turn of turns) {
    if (!turn.items) {
      nested.push(turn);
      continue;
    }
    const items = turn.items.filter((item) => {
      if (item.type !== "tool_call" || !item.toolCall.parentId) return true;
      const parent = calls.get(item.toolCall.parentId);
      if (!parent || parent === item.toolCall) return true;
      parent.children = [...(parent.children ?? []), item.toolCall];
      moved = true;
      return false;
    });
    if (items.length === turn.items.length) {
      nested.push(turn);
    } else if (items.length > 0 || turn.finalResponse || turn.isStreaming) {
      nested.push({ ...turn, items: items.length > 0 ? items : undefined });
    }
  }
  return moved ? nested : turns;
};

/**
 * ``runActive`` says whether the agent can still be running: this tab streams
 * it, or the server reports it. A call with no result then waits; else it
 * shows as stopped.
 */
export const useTraceTurns = (
  messages: Message[],
  streamingState: StreamingState | undefined,
  runActive = true,
): TraceTurn[] => {
  return useMemo(() => {
    const turns = buildTraceTurnsFromMessages(messages, runActive);

    if (!streamingState) return nestSubagentCalls(turns);

    const holdStream =
      streamingState.isStreaming || streamingState.fadeHold;

    const persistedHasUser =
      !!streamingState.userMessageId &&
      messages.some(
        (m) => m.id === streamingState.userMessageId && m.role === "user",
      );

    if (streamingState.streamingUserMessage && !persistedHasUser) {
      const userMsg = streamingState.streamingUserMessage;
      turns.push({
        id: streamingState.userMessageId ?? userMsg.id,
        type: "user",
        content: userMsg.content ?? "",
      });
    }

    const streamingTurn = buildStreamingTurn(streamingState, runActive);
    if (!streamingTurn) return nestSubagentCalls(turns);

    if (holdStream) {
      const userId =
        streamingState.userMessageId ??
        streamingState.streamingUserMessage?.id;
      if (userId) {
        const userIdx = turns.findLastIndex(
          (t) => t.type === "user" && t.id === userId,
        );
        if (userIdx >= 0) {
          turns.splice(userIdx + 1);
        }
      }
      turns.push(streamingTurn);
      return nestSubagentCalls(turns);
    }

    const lastPersistedAssistant = [...messages]
      .reverse()
      .find((m) => m.role === "assistant");
    const isDuplicate =
      lastPersistedAssistant &&
      !streamingState.isStreaming &&
      streamingState.streamingMessage?.content ===
        lastPersistedAssistant.content;

    if (!isDuplicate) {
      turns.push(streamingTurn);
    }

    return nestSubagentCalls(turns);
  }, [messages, streamingState, runActive]);
};
